import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  Radio,
  Select,
  Input,
  Button,
  Table,
  Tag,
  Space,
  Statistic,
  Row,
  Col,
  Alert,
  Spin,
  Empty,
  Tooltip,
  message,
  Modal
} from 'antd';
import {
  SearchOutlined,
  DeleteOutlined,
  PlusOutlined,
  InfoCircleOutlined,
  ShopOutlined,
  TeamOutlined,
  EnvironmentOutlined
} from '@ant-design/icons';
import type {
  ScopeConfig,
  ScopeType,
  StoreGroup,
  Store,
  ValidationError,
  PlanogramTemplate
} from '../../../../types/plan-template';
import { getCurrentTimestamp } from '../../../../utils/dateTimeUtil';
import { ReferenceDataService } from '../../../../services/referenceDataService';
import storeDisplayGroupService from '../../../../services/storeDisplayGroupService';
import {warehouseService} from '../../../../services/warehouseService';

interface ScopeConfigurationProps {
  templateData: Partial<PlanogramTemplate>;
  onDataChange: (updates: Partial<PlanogramTemplate>) => void;
  onValidationChange: (isValid: boolean, errors?: ValidationError[]) => void;
  onNext: () => void;
  onPrevious: () => void;
}

interface ScopeStatistics {
  totalStores: number;
  storesByRegion: Array<{ region: string; count: number }>;
  storesByType: Array<{ type: string; count: number }>;
  affectedRegions: string[];
  affectedStoreTypes: string[];
}

const ScopeConfiguration: React.FC<ScopeConfigurationProps> = ({
  templateData,
  onDataChange,
  onValidationChange,
}) => {
  const { t } = useTranslation();
  
  // 添加防抖引用
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const validationDebounceTimer = useRef<NodeJS.Timeout | null>(null);
  
  // 添加缓存引用
  const statisticsCache = useRef<{
    key: string;
    data: ScopeStatistics;
  } | null>(null);
  
  // 添加验证缓存引用
  const validationCache = useRef<{
    key: string;
    isValid: boolean;
    errors: ValidationError[];
  } | null>(null);

  
  // Store group selection columns
  const storeGroupColumns = [
    {
      title: t('planTemplate.wizard.scopeConfiguration.storeGroupSelection.columns.code'),
      dataIndex: 'code',
      key: 'code',
      width: 170
    },
    {
      title: t('planTemplate.wizard.scopeConfiguration.storeGroupSelection.columns.name'),
      dataIndex: 'name',
      key: 'name',
      width: 170
    },
    {
      title: t('planTemplate.wizard.scopeConfiguration.storeGroupSelection.columns.displayGroup'),
      dataIndex: 'displayGroupCode',
      key: 'displayGroupCode',
      width: 120,
      render: (code: string) => <Tag color="blue">{code}</Tag>
    },
    {
      title: t('planTemplate.wizard.scopeConfiguration.storeGroupSelection.columns.storeCount'),
      dataIndex: 'shopCodes',
      key: 'shopCodes',
      width: 100,
      render: (shopCodes: string[]) => (
        <span style={{ fontWeight: 'bold', color: '#1890ff' }}>
          {shopCodes?.length || 0}
        </span>
      )
    },
    // {
    //   title: t('planTemplate.wizard.scopeConfiguration.storeGroupSelection.columns.description'),
    //   dataIndex: 'description',
    //   key: 'description',
    //   ellipsis: true
    // }
  ];

  // Store selection columns
  const storeColumns = [
    {
      title: t('planTemplate.wizard.scopeConfiguration.table.storeCode'),
      dataIndex: 'code',
      key: 'code',
      width: 120
    },
    {
      title: t('planTemplate.wizard.scopeConfiguration.table.storeName'),
      dataIndex: 'name',
      key: 'name',
      ellipsis: true
    },
    {
      title: t('planTemplate.wizard.scopeConfiguration.table.area'),
      dataIndex: 'area',
      key: 'area',
      width: 100,
      render: (area: string) => <Tag color="blue">{area}</Tag>
    },
    {
      title: t('planTemplate.wizard.scopeConfiguration.table.format'),
      dataIndex: 'format',
      key: 'format',
      width: 100,
      render: (format: string) => <Tag color="green">{format}</Tag>
    },
    {
      title: t('planTemplate.wizard.scopeConfiguration.table.clusterSection'),
      dataIndex: 'clusterSection',
      key: 'clusterSection',
      width: 100,
      render: (section: string) => <Tag color="orange">{section}</Tag>
    },
    // {
    //   title: t('planTemplate.wizard.scopeConfiguration.table.detailAddress'),
    //   dataIndex: 'detailAddress',
    //   key: 'detailAddress',
    //   ellipsis: true
    // }
  ];
  
  // State management
  const [scopeConfig, setScopeConfig] = useState<ScopeConfig>(() => ({
    scopeType: 'All Stores',
    storeGroupList: [],
    customStoreList: [],
    storeDisplayGroupList: [],
    storeSelectionCriteria: {},
    excludedStores: [],
    storeCount: 0,
    scopeValidationStatus: 'Not Validated',
    lastScopeUpdate: getCurrentTimestamp(),
    ...templateData.scopeDefinition
  }));

  const [loading, setLoading] = useState(false);
  const [storeGroups, setStoreGroups] = useState<StoreGroup[]>([]);
  const [availableStores, setAvailableStores] = useState<Store[]>([]);
  const [selectedStores, setSelectedStores] = useState<Store[]>([]);
  const [excludedStoreDetails, setExcludedStoreDetails] = useState<Store[]>([]);
  const [storeDisplayGroups, setStoreDisplayGroups] = useState<any[]>([]);
  const [selectedStoreDisplayGroups, setSelectedStoreDisplayGroups] = useState<any[]>([]);
  const [scopeStatistics, setScopeStatistics] = useState<ScopeStatistics>({
    totalStores: 0,
    storesByRegion: [],
    storesByType: [],
    affectedRegions: [],
    affectedStoreTypes: []
  });

  // Search states
  const [storeGroupSearch, setStoreGroupSearch] = useState('');
  const [storeSearch, setStoreSearch] = useState('');
  const [excludeStoreSearch, setExcludeStoreSearch] = useState('');
  const [storeDisplayGroupSearch, setStoreDisplayGroupSearch] = useState('');

  // Modal states
  const [storeSelectionVisible, setStoreSelectionVisible] = useState(false);
  const [excludeStoreVisible, setExcludeStoreVisible] = useState(false);


  // Load initial data
  useEffect(() => {
    loadInitialData();
  }, []);

  // Update parent component when scope config changes
  useEffect(() => {
    onDataChange({
      scopeDefinition: scopeConfig
    });
  }, [scopeConfig, onDataChange]);

  // Calculate scope statistics when relevant data changes
  useEffect(() => {
    calculateScopeStatistics();
  }, [scopeConfig, scopeConfig.scopeType, scopeConfig.storeDisplayGroupList, scopeConfig.storeGroupList, scopeConfig.customStoreList, scopeConfig.excludedStores, templateData.planType]);

  // Validate configuration when statistics change
  useEffect(() => {
    validateScopeConfiguration();
  }, [scopeStatistics, scopeConfig.scopeType, scopeConfig.storeDisplayGroupList, scopeConfig.storeGroupList, scopeConfig.customStoreList, scopeConfig.excludedStores, templateData.planType]);


  const loadInitialData = async () => {
    setLoading(true);
    try {
      const [storeGroupsResponse, storesResponse, storeDisplayGroupsResponse] = await Promise.all([
        ReferenceDataService.getStoreGroups(),
        ReferenceDataService.getStores({"statuses": ["active"]}),
        storeDisplayGroupService.getStoreDisplayGroups({}, { page: 1, pageSize: 1000 })
      ]);

      if (storeGroupsResponse.success) {
        setStoreGroups(storeGroupsResponse.data);
      }

      if (storesResponse.success) {
        setAvailableStores(storesResponse.data);
      }

      if (storeDisplayGroupsResponse.success) {
         setStoreDisplayGroups(storeDisplayGroupsResponse.data.tableData || []);
      }

      // Load selected stores if scope config has store selections
      if (scopeConfig.customStoreList.length > 0) {
        const selectedStoresResponse = await ReferenceDataService.getStoresByCodes(
          scopeConfig.customStoreList
        );
        if (selectedStoresResponse.success) {
          setSelectedStores(selectedStoresResponse.data);
        }
      }

      // Load excluded stores if any
      if (scopeConfig.excludedStores.length > 0) {
        const excludedStoresResponse = await ReferenceDataService.getStoresByCodes(
          scopeConfig.excludedStores
        );
        if (excludedStoresResponse.success) {
          setExcludedStoreDetails(excludedStoresResponse.data);
        }
      }
    } catch (error) {
      message.error(t('planTemplate.wizard.scopeConfiguration.validation.loadDataFailed'));
    } finally {
      setLoading(false);
    }
  };

  const validateScopeConfiguration = useCallback(async () => {
    // 生成验证缓存键
    const validationCacheKey = JSON.stringify({
      scopeStatistics: scopeStatistics,
      scopeType: scopeConfig.scopeType,
      storeDisplayGroupList: scopeConfig.storeDisplayGroupList,
      storeGroupList: scopeConfig.storeGroupList,
      customStoreList: scopeConfig.customStoreList,
      excludedStores: scopeConfig.excludedStores,
      planType: templateData.planType
    });

    // 检查验证缓存
    if (validationCache.current && validationCache.current.key === validationCacheKey) {
      onValidationChange(validationCache.current.isValid, validationCache.current.errors);
      return;
    }

    const errors: ValidationError[] = [];

    // Validate scope type selection
    if (!scopeConfig.scopeType) {
      errors.push({
        field: 'scopeType',
        message: t('planTemplate.wizard.scopeConfiguration.validation.scopeTypeRequired'),
        type: 'error'
      });
    }

    // Validate specific scope type requirements
    switch (scopeConfig.scopeType) {
      case 'Specific Store Groups':
        if (scopeConfig.storeGroupList.length === 0) {
          errors.push({
            field: 'storeGroupList',
            message: t('planTemplate.wizard.scopeConfiguration.validation.storeGroupRequired'),
            type: 'error'
          });
        } else {
          // Validate store group availability
          try {
            const response = await ReferenceDataService.getStoresByGroupIds(
              scopeConfig.storeGroupList
            );
            if (!response.success || response.data.length === 0) {
              errors.push({
                field: 'storeGroupList',
                message: t('planTemplate.wizard.scopeConfiguration.validation.storeGroupNoStores'),
                type: 'error'
              });
            }
          } catch (error) {
            errors.push({
              field: 'storeGroupList',
              message: t('planTemplate.wizard.scopeConfiguration.validation.storeGroupValidationFailed'),
              type: 'error'
            });
          }
        }
        break;

      case 'Specific Stores':
        if (!scopeConfig.storeDisplayGroupList || scopeConfig.storeDisplayGroupList.length === 0) {
          errors.push({
            field: 'storeDisplayGroupList',
            message: t('planTemplate.wizard.scopeConfiguration.validation.storeDisplayGroupRequired'),
            type: 'error'
          });
        }
        break;

      case 'Custom Store Collections':
      case 'Individual Stores':
        if (scopeConfig.customStoreList.length === 0) {
          errors.push({
            field: 'customStoreList',
            message: t('planTemplate.wizard.scopeConfiguration.validation.storeRequired'),
            type: 'error'
          });
        } else {
          // Validate selected stores availability
          try {
            const response = await ReferenceDataService.getStoresByCodes(
              scopeConfig.customStoreList
            );
            if (response.success) {
              const validStores = response.data;
              const invalidIds = scopeConfig.customStoreList.filter(
                id => !validStores.some(store => store.id === id)
              );
              if (invalidIds.length > 0) {
                errors.push({
                  field: 'customStoreList',
                  message: t('planTemplate.wizard.scopeConfiguration.validation.storeUnavailable', {
                    count: invalidIds.length
                  }),
                  type: 'error'
                });
              }
            }
          } catch (error) {
            errors.push({
              field: 'customStoreList',
              message: t('planTemplate.wizard.scopeConfiguration.validation.storeValidationFailed'),
              type: 'error'
            });
          }
        }
        break;
    }

    // Validate store count
    if (scopeStatistics.totalStores === 0 && scopeConfig.scopeType !== 'All Stores') {
      errors.push({
        field: 'storeCount',
        message: t('planTemplate.wizard.scopeConfiguration.validation.emptyScopeError'),
        type: 'error'
      });
    }

    // Validate excluded stores
    if (scopeConfig.excludedStores.length > 0) {
      try {
        const response = await ReferenceDataService.getStoresByCodes(
          scopeConfig.excludedStores
        );
        if (response.success) {
          const validStores = response.data;
          const invalidIds = scopeConfig.excludedStores.filter(
            code => !validStores.some(store => store.code === code)
          );
          if (invalidIds.length > 0) {
            errors.push({
              field: 'excludedStores',
              message: t('planTemplate.wizard.scopeConfiguration.validation.excludedStoreUnavailable', {
                count: invalidIds.length
              }),
              type: 'error'
            });
          }
        }
      } catch (error) {
        errors.push({
          field: 'excludedStores',
          message: t('planTemplate.wizard.scopeConfiguration.validation.excludedStoreValidationFailed'),
          type: 'error'
        });
      }
    }

    const isValid = errors.length === 0;
    
    // Update scope validation status
    setScopeConfig(prev => ({
      ...prev,
      scopeValidationStatus: isValid ? 'Validation Passed' : 'Validation Failed',
      storeCount: scopeStatistics.totalStores
    }));

    // 更新验证缓存
    validationCache.current = {
      key: validationCacheKey,
      isValid,
      errors
    };

    onValidationChange(isValid, errors);
  }, [scopeStatistics, scopeConfig.scopeType, scopeConfig.storeDisplayGroupList, scopeConfig.storeGroupList, scopeConfig.customStoreList, scopeConfig.excludedStores, templateData.planType, onValidationChange]);

  const calculateScopeStatistics = useCallback(async () => {
    // 生成缓存键
    const cacheKey = JSON.stringify({
      scopeType: scopeConfig.scopeType,
      storeDisplayGroupList: scopeConfig.storeDisplayGroupList,
      storeGroupList: scopeConfig.storeGroupList,
      customStoreList: scopeConfig.customStoreList,
      excludedStores: scopeConfig.excludedStores
    });

    // 检查缓存
    if (statisticsCache.current && statisticsCache.current.key === cacheKey) {
      setScopeStatistics(statisticsCache.current.data);
      return;
    }

    let affectedStores: Store[] = [];

    try {
      switch (scopeConfig.scopeType) {
        case 'All Stores':
          affectedStores = availableStores;
          break;

        case 'Specific Store Groups':
          if (scopeConfig.storeGroupList.length > 0) {
            const response = await ReferenceDataService.getStoresByGroupIds(
              scopeConfig.storeGroupList
            );
            if (response.success) {
              affectedStores = response.data;
            }
          }
          break;
        case 'Specific Stores':
          if (scopeConfig.storeDisplayGroupList && scopeConfig.storeDisplayGroupList.length > 0) {
            let storeCodes = scopeConfig.storeDisplayGroupList.map(group => group.storeCode);
            let response = await warehouseService.multiQueryWarehouse({codes: storeCodes});
            if (response.success) {
              affectedStores = response.data;
            }
          }
          break;
        case 'Custom Store Collections':
        case 'Individual Stores':
          affectedStores = selectedStores;
          break;
      }

      // Remove excluded stores
      if (scopeConfig.excludedStores.length > 0) {
        affectedStores = affectedStores.filter(
          store => !scopeConfig.excludedStores.includes(store.id)
        );
      }

      // Calculate statistics
      const areaCounts = affectedStores.reduce((acc, store) => {
        acc[store.clusterSection] = (acc[store.clusterSection] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);

      const formatCounts = affectedStores.reduce((acc, store) => {
        acc[store.format] = (acc[store.format] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);

      const stats: ScopeStatistics = {
        totalStores: affectedStores.length,
        storesByRegion: Object.entries(areaCounts).map(([area, count]) => ({
          region: area,
          count: count as number
        })),
        storesByType: Object.entries(formatCounts).map(([format, count]) => ({
          type: format,
          count: count as number
        })),
        affectedRegions: Object.keys(areaCounts),
        affectedStoreTypes: Object.keys(formatCounts)
      };

      // 更新缓存
      statisticsCache.current = {
        key: cacheKey,
        data: stats
      };

      setScopeStatistics(stats);
    } catch (error) {
      console.error(t('planTemplate.wizard.scopeConfiguration.validation.calculateStatsFailed'), error);
    }
  }, [scopeConfig.scopeType, scopeConfig.storeDisplayGroupList, scopeConfig.storeGroupList, scopeConfig.excludedStores, availableStores, selectedStores]);

  const handleScopeTypeChange = (scopeType: ScopeType) => {
    setScopeConfig(prev => ({
      ...prev,
      scopeType,
      // Reset selections when changing scope type
      storeGroupList: scopeType === 'Specific Store Groups' ? prev.storeGroupList : [],
      customStoreList: ['Custom Store Collections', 'Individual Stores'].includes(scopeType) 
        ? prev.customStoreList : [],
      storeDisplayGroupList: scopeType === 'Specific Stores' ? prev.storeDisplayGroupList || [] : [],
      lastScopeUpdate: getCurrentTimestamp()
    }));

    // Clear selected stores if not needed
    if (!['Custom Store Collections', 'Individual Stores'].includes(scopeType)) {
      setSelectedStores([]);
    }
    
    // Clear selected store display groups when changing scope type
    if (scopeType !== 'Specific Stores') {
      setSelectedStoreDisplayGroups([]);
    }
  };

  const handleStoreGroupSelection = (selectedGroupIds: string[]) => {
    setScopeConfig(prev => ({
      ...prev,
      storeGroupList: selectedGroupIds,
      lastScopeUpdate: getCurrentTimestamp()
    }));
  };

  const handleStoreSelection = (stores: Store[]) => {
    setSelectedStores(stores);
    setScopeConfig(prev => ({
      ...prev,
      customStoreList: stores.map(store => store.id),
      lastScopeUpdate: getCurrentTimestamp()
    }));
  };

  const handleExcludeStore = (storeIds: string[]) => {
    setScopeConfig(prev => ({
      ...prev,
      excludedStores: [...prev.excludedStores, ...storeIds],
      lastScopeUpdate: getCurrentTimestamp()
    }));

    // Update excluded store details
    const newExcludedStores = availableStores.filter(store => 
      storeIds.includes(store.code)
    );
    setExcludedStoreDetails(prev => [...prev, ...newExcludedStores]);
  };

  const handleRemoveExcludedStore = (storeId: string) => {
    setScopeConfig(prev => ({
      ...prev,
      excludedStores: prev.excludedStores.filter(code => code !== storeId),
      lastScopeUpdate: getCurrentTimestamp()
    }));

    setExcludedStoreDetails(prev => 
      prev.filter(store => store.code !== storeId)
    );
  };

  // Filtered data for display
  const filteredStoreGroups = useMemo(() => {
    if (!storeGroupSearch) return storeGroups;
    const search = storeGroupSearch.toLowerCase();
    return storeGroups.filter(group =>
      group.name.toLowerCase().includes(search) ||
      group.code.toLowerCase().includes(search) ||
      group.id.toLowerCase().includes(search)
    );
  }, [storeGroups, storeGroupSearch]);

  const filteredAvailableStores = useMemo(() => {
    if (!storeSearch) return availableStores;
    const search = storeSearch.toLowerCase();
    return availableStores.filter(store =>
      store.name.toLowerCase().includes(search) ||
      store.code.toLowerCase().includes(search) ||
      store.detailAddress?.toLowerCase().includes(search)
    );
  }, [availableStores, storeSearch]);

  const filteredExcludeStores = useMemo(() => {
    if (!excludeStoreSearch) return availableStores;
    const search = excludeStoreSearch.toLowerCase();
    return availableStores.filter(store =>
      store.name.toLowerCase().includes(search) ||
      store.code.toLowerCase().includes(search) ||
      store.detailAddress?.toLowerCase().includes(search)
    );
  }, [availableStores, excludeStoreSearch]);

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '400px' 
      }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ marginBottom: '8px' }}>{t('planTemplate.wizard.scopeConfiguration.title')}</h2>
        <p style={{ color: '#666', marginBottom: '24px' }}>
          {t('planTemplate.wizard.scopeConfiguration.description')}
        </p>
      </div>

      {/* Scope Type Selection */}
      <Card title={t('planTemplate.wizard.scopeConfiguration.scopeTypeSelection.title')} style={{ marginBottom: '24px' }}>
        <Radio.Group
          value={scopeConfig.scopeType}
          onChange={(e) => handleScopeTypeChange(e.target.value)}
          style={{ width: '100%' }}
        >
          <Space direction="vertical" style={{ width: '100%' }}>
            {/* <Radio value="All Stores">
              <Space>
                <ShopOutlined />
                <span>{t('planTemplate.wizard.scopeConfiguration.scopeTypeSelection.allStores')}</span>
                <Tooltip title={t('planTemplate.wizard.scopeConfiguration.scopeTypeSelection.allStoresTooltip')}>
                  <InfoCircleOutlined style={{ color: '#999' }} />
                </Tooltip>
              </Space>
            </Radio> */}
            <Radio value="Specific Store Groups">
              <Space>
                <TeamOutlined />
                <span>{t('planTemplate.wizard.scopeConfiguration.scopeTypeSelection.specificStoreGroups')}</span>
                <Tooltip title={t('planTemplate.wizard.scopeConfiguration.scopeTypeSelection.specificStoreGroupsTooltip')}>
                  <InfoCircleOutlined style={{ color: '#999' }} />
                </Tooltip>
              </Space>
            </Radio>
            <Radio value="Specific Stores">
              <Space>
                <ShopOutlined />
                <span>{t('planTemplate.wizard.scopeConfiguration.scopeTypeSelection.specificStores')}</span>
                <Tooltip title={t('planTemplate.wizard.scopeConfiguration.scopeTypeSelection.specificStoresTooltip')}>
                  <InfoCircleOutlined style={{ color: '#999' }} />
                </Tooltip>
              </Space>
            </Radio>
          </Space>
        </Radio.Group>
      </Card>

      {/* Store Group Selection */}
      {scopeConfig.scopeType === 'Specific Store Groups' && (
        <Card title={t('planTemplate.wizard.scopeConfiguration.storeGroupSelection.title')} style={{ marginBottom: '24px' }}>
          <div style={{ marginBottom: '16px' }}>
            <Space>
              <Input
                placeholder={t('planTemplate.wizard.scopeConfiguration.storeGroupSelection.searchPlaceholder')}
                prefix={<SearchOutlined />}
                value={storeGroupSearch}
                onChange={(e) => setStoreGroupSearch(e.target.value)}
                style={{ width: '300px' }}
              />
            </Space>
          </div>
          
          <Table
            rowSelection={{
              type: 'checkbox',
              selectedRowKeys: scopeConfig.storeGroupList,
              onChange: (selectedRowKeys) => 
                handleStoreGroupSelection(selectedRowKeys as string[]),
              getCheckboxProps: (record: StoreGroup) => ({
                name: record.name,
              })
            }}
            columns={storeGroupColumns}
            dataSource={filteredStoreGroups || []}
            rowKey="id"
            // pagination={{
            //   // pageSize: 10,
            //   showSizeChanger: true,
            //   showQuickJumper: true,
            //   showTotal: (total, range) => 
            //     t('planTemplate.wizard.scopeConfiguration.table.pagination', {
            //       start: range[0],
            //       end: range[1],
            //       total
            //     })
            // }}
            scroll={{ y: 500 }}
            size="small"
          />
        </Card>
      )}

      {/* Store Display Group Selection */}
      {scopeConfig.scopeType === 'Specific Stores' && (
        <Card title={t('planTemplate.wizard.scopeConfiguration.storeDisplayGroupSelection.title')} style={{ marginBottom: '24px' }}>
          <div style={{ marginBottom: '16px' }}>
            <Input
              placeholder={t('planTemplate.wizard.scopeConfiguration.storeDisplayGroupSelection.searchPlaceholder')}
              prefix={<SearchOutlined />}
              value={storeDisplayGroupSearch}
              onChange={(e) => setStoreDisplayGroupSearch(e.target.value)}
              style={{ width: '300px' }}
            />
          </div>
          
          <Table
            rowSelection={{
              type: 'checkbox',
              selectedRowKeys: scopeConfig.storeDisplayGroupList.map(g => `${g.storeCode}-${g.displayGroupCode}`),
              onChange: (selectedRowKeys) => {
                const selected = storeDisplayGroups.filter(g => selectedRowKeys.includes(`${g.storeCode}-${g.displayGroupCode}`));
                setSelectedStoreDisplayGroups(selected);
                // Update scope config with selected store display groups
                setScopeConfig(prev => ({
                  ...prev,
                  storeDisplayGroupList: selected
                }));
              },
              getCheckboxProps: (record: any) => ({
                name: record.name,
              })
            }}
            columns={[
              {
                title: t('planTemplate.wizard.scopeConfiguration.storeDisplayGroupSelection.columns.displayGroupCode'),
                dataIndex: 'code',
                key: 'code',
                width: 150
              },
              {
                title: t('planTemplate.wizard.scopeConfiguration.storeDisplayGroupSelection.columns.displayGroupName'),
                dataIndex: 'name',
                key: 'name',
                ellipsis: true
              },
              {
                title: t('planTemplate.wizard.scopeConfiguration.storeDisplayGroupSelection.columns.storeCode'),
                dataIndex: 'storeCode',
                key: 'storeCode',
                width: 120
              },
              {
                title: t('planTemplate.wizard.scopeConfiguration.storeDisplayGroupSelection.columns.storeName'),
                dataIndex: 'storeName',
                key: 'storeName',
                ellipsis: true
              }
            ]}
            dataSource={storeDisplayGroups.filter(group => 
              !storeDisplayGroupSearch || 
              group.name.toLowerCase().includes(storeDisplayGroupSearch.toLowerCase()) ||
              group.code.toLowerCase().includes(storeDisplayGroupSearch.toLowerCase()) ||
              (group.storeCode && group.storeCode.toLowerCase().includes(storeDisplayGroupSearch.toLowerCase()))
            )}
            rowKey={(record) => `${record.storeCode}-${record.displayGroupCode}`}
            // pagination={{
            //   pageSize: 10,
            //   showSizeChanger: true,
            //   showQuickJumper: true,
            //   showTotal: (total, range) => 
            //     `${range[0]}-${range[1]} of ${total} items`
            // }}
            size="small"
            scroll={{ y: 500 }}
          />
        </Card>
      )}

      {/* Store Exclusion */}
      <Card title={t('planTemplate.wizard.scopeConfiguration.storeExclusion.title')} style={{ marginBottom: '24px' }}>
          <div style={{ marginBottom: '16px' }}>
            <Space>
              <Button
                icon={<PlusOutlined />}
                onClick={() => setExcludeStoreVisible(true)}
              >
                {t('planTemplate.wizard.scopeConfiguration.storeExclusion.addButton')}
              </Button>
              {excludedStoreDetails.length > 0 && (
                <span style={{ color: '#666' }}>
                  {t('planTemplate.wizard.scopeConfiguration.storeExclusion.excludedCount', {
                    count: excludedStoreDetails.length
                  })}
                </span>
              )}
            </Space>
          </div>

          {excludedStoreDetails.length > 0 && (
            <div style={{ marginBottom: '16px' }}>
              <Space wrap>
                {excludedStoreDetails.map(store => (
                  <Tag
                    key={store.code}
                    closable
                    onClose={() => handleRemoveExcludedStore(store.code)}
                  >
                    {store.name}
                  </Tag>
                ))}
              </Space>
            </div>
          )}
        </Card>

      {/* Scope Statistics */}
      <Card title={t('planTemplate.wizard.scopeConfiguration.statistics.title')} style={{ marginBottom: '24px' }}>
        <Row gutter={[16, 16]}>
          <Col span={6}>
            <Statistic
              title={t('planTemplate.wizard.scopeConfiguration.statistics.totalStores')}
              value={scopeStatistics.totalStores}
              prefix={<ShopOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title={t('planTemplate.wizard.scopeConfiguration.statistics.affectedRegions')}
              value={scopeStatistics.affectedRegions.length}
              prefix={<EnvironmentOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title={t('planTemplate.wizard.scopeConfiguration.statistics.storeTypes')}
              value={scopeStatistics.affectedStoreTypes.length}
              prefix={<TeamOutlined />}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title={t('planTemplate.wizard.scopeConfiguration.statistics.excludedStores')}
              value={excludedStoreDetails.length}
              prefix={<DeleteOutlined />}
              valueStyle={{ color: '#f5222d' }}
            />
          </Col>
        </Row>

        {/* Detailed Statistics */}
        {scopeStatistics.totalStores > 0 && (
          <div style={{ marginTop: '24px' }}>
            <Row gutter={[24, 16]}>
              <Col span={12}>
                <div>
                  <h4 style={{ marginBottom: '12px' }}>{t('planTemplate.wizard.scopeConfiguration.statistics.byRegion')}</h4>
                  {scopeStatistics.storesByRegion.length > 0 ? (
                    <Space wrap>
                      {scopeStatistics.storesByRegion.map(({ region, count }) => (
                        <Tag key={region} color="blue" style={{ marginBottom: '4px' }}>
                          {region}: {count} ({((count / scopeStatistics.totalStores) * 100).toFixed(1)}%)
                        </Tag>
                      ))}
                    </Space>
                  ) : (
                    <span style={{ color: '#999' }}>{t('planTemplate.wizard.scopeConfiguration.statistics.noData')}</span>
                  )}
                </div>
              </Col>
              <Col span={12}>
                <div>
                  <h4 style={{ marginBottom: '12px' }}>{t('planTemplate.wizard.scopeConfiguration.statistics.byStoreType')}</h4>
                  {scopeStatistics.storesByType.length > 0 ? (
                    <Space wrap>
                      {scopeStatistics.storesByType.map(({ type, count }) => (
                        <Tag key={type} color="green" style={{ marginBottom: '4px' }}>
                          {type}: {count} ({((count / scopeStatistics.totalStores) * 100).toFixed(1)}%)
                        </Tag>
                      ))}
                    </Space>
                  ) : (
                    <span style={{ color: '#999' }}>{t('planTemplate.wizard.scopeConfiguration.statistics.noData')}</span>
                  )}
                </div>
              </Col>
            </Row>
          </div>
        )}

        {/* Scope Summary */}
        {scopeStatistics.totalStores > 0 && (
          <div style={{ 
            marginTop: '24px', 
            padding: '16px', 
            background: '#f6ffed', 
            border: '1px solid #b7eb8f',
            borderRadius: '6px'
          }}>
            <h4 style={{ color: '#52c41a', marginBottom: '8px' }}>{t('planTemplate.wizard.scopeConfiguration.statistics.summary.title')}</h4>
            <p style={{ margin: 0, color: '#666' }}>
              {t('planTemplate.wizard.scopeConfiguration.statistics.summary.description', {
                totalStores: scopeStatistics.totalStores,
                regionCount: scopeStatistics.affectedRegions.length,
                regions: scopeStatistics.affectedRegions.join('、'),
                storeTypeCount: scopeStatistics.affectedStoreTypes.length,
                storeTypes: scopeStatistics.affectedStoreTypes.join('、'),
                excludedText: excludedStoreDetails.length > 0 
                  ? t('planTemplate.wizard.scopeConfiguration.statistics.summary.excludedText', {
                      count: excludedStoreDetails.length
                    })
                  : ''
              })}
            </p>
          </div>
        )}
      </Card>

      {/* Scope Configuration Preview */}
      {scopeStatistics.totalStores > 0 && (
        <Card title={t('planTemplate.wizard.scopeConfiguration.configPreview.title')} style={{ marginBottom: '24px' }}>
          <div style={{ background: '#fafafa', padding: '16px', borderRadius: '6px' }}>
            <h4 style={{ marginBottom: '12px', color: '#1890ff' }}>{t('planTemplate.wizard.scopeConfiguration.configPreview.summaryTitle')}</h4>
            
            <div style={{ marginBottom: '12px' }}>
              <strong>{t('planTemplate.wizard.scopeConfiguration.configPreview.scopeType')}</strong>
              <Tag color="blue" style={{ marginLeft: '8px' }}>
                {scopeConfig.scopeType === 'All Stores' && t('planTemplate.wizard.scopeConfiguration.scopeTypeSelection.allStores')}
                {scopeConfig.scopeType === 'Specific Store Groups' && t('planTemplate.wizard.scopeConfiguration.scopeTypeSelection.specificStoreGroups')}
              </Tag>
            </div>

            {scopeConfig.scopeType === 'Specific Store Groups' && scopeConfig.storeGroupList.length > 0 && (
              <div style={{ marginBottom: '12px' }}>
                <strong>{t('planTemplate.wizard.scopeConfiguration.configPreview.selectedStoreGroups')}</strong>
                <div style={{ marginTop: '4px' }}>
                  {storeGroups
                    .filter(group => scopeConfig.storeGroupList.includes(group.id))
                    .map(group => (
                      <Tag key={group.id} color="green" style={{ marginBottom: '4px' }}>
                        {group.name} ({group.shopCodes?.length || 0}Stores)
                      </Tag>
                    ))
                  }
                </div>
              </div>
            )}

            {excludedStoreDetails.length > 0 && (
              <div style={{ marginBottom: '12px' }}>
                <strong>{t('planTemplate.wizard.scopeConfiguration.configPreview.excludedStores')}</strong>
                <span style={{ marginLeft: '8px', color: '#f5222d' }}>
                  {excludedStoreDetails.length} Stores
                  {excludedStoreDetails.length <= 3 && (
                    <>
                      （{excludedStoreDetails.map(s => s.name).join('、')}）
                    </>
                  )}
                </span>
              </div>
            )}

            <div style={{ marginBottom: '12px' }}>
              <strong>{t('planTemplate.wizard.scopeConfiguration.configPreview.statistics')}</strong>
              <div style={{ marginTop: '4px' }}>
                <Space>
                  <span>{t('planTemplate.wizard.scopeConfiguration.configPreview.totalStoresLabel')}: <strong style={{ color: '#1890ff' }}>{scopeStatistics.totalStores}</strong></span>
                  <span>{t('planTemplate.wizard.scopeConfiguration.configPreview.affectedRegionsLabel')}: <strong style={{ color: '#52c41a' }}>{scopeStatistics.affectedRegions.length}</strong></span>
                  <span>{t('planTemplate.wizard.scopeConfiguration.configPreview.storeTypesLabel')}: <strong style={{ color: '#fa8c16' }}>{scopeStatistics.affectedStoreTypes.length}</strong></span>
                </Space>
              </div>
            </div>

            <div>
              <strong>{t('planTemplate.wizard.scopeConfiguration.configPreview.lastUpdate')}</strong>
              <span style={{ marginLeft: '8px', color: '#666' }}>
                {new Date(scopeConfig.lastScopeUpdate).toLocaleString()}
              </span>
            </div>
          </div>
        </Card>
      )}

      {/* Validation Status */}
      {scopeConfig.scopeValidationStatus === 'Validation Failed' && (
        <Alert
          type="error"
          message={t('planTemplate.wizard.scopeConfiguration.validation.configValidationFailed')}
          description={t('planTemplate.wizard.scopeConfiguration.validation.configValidationFailedDesc')}
          style={{ marginBottom: '24px' }}
          showIcon
        />
      )}

      {scopeConfig.scopeValidationStatus === 'Validation Passed' && (
        <Alert
          type="success"
          message={t('planTemplate.wizard.scopeConfiguration.validation.configValidationPassed')}
          description={t('planTemplate.wizard.scopeConfiguration.validation.configValidationPassedDesc', {
            totalStores: scopeStatistics.totalStores,
            regionCount: scopeStatistics.affectedRegions.length
          })}
          style={{ marginBottom: '24px' }}
          showIcon
        />
      )}

      {/* Business Rule Warnings */}
      {scopeStatistics.totalStores > 0 && templateData.planType && (
        <div style={{ marginBottom: '24px' }}>
          {templateData.planType === 'Major Plans' && scopeStatistics.totalStores < 50 && (
            <Alert
              type="warning"
              message="Tips"
              description={t('planTemplate.wizard.scopeConfiguration.businessRules.majorPlanMinStores')}
              style={{ marginBottom: '8px' }}
              showIcon
            />
          )}
          
          {templateData.planType === 'Major Plans' && scopeStatistics.affectedRegions.length < 2 && (
            <Alert
              type="info"
              message="Scopes"
              description={t('planTemplate.wizard.scopeConfiguration.businessRules.majorPlanMultiRegion')}
              style={{ marginBottom: '8px' }}
              showIcon
            />
          )}

          {templateData.planType === 'Minor Plans' && scopeStatistics.totalStores > 20 && (
            <Alert
              type="warning"
              message="Scopes"
              description={t('planTemplate.wizard.scopeConfiguration.businessRules.minorPlanTooManyStores')}
              style={{ marginBottom: '8px' }}
              showIcon
            />
          )}
        </div>
      )}

      {/* Store Selection Modal */}
      <Modal
        title={t('planTemplate.wizard.scopeConfiguration.modals.selectStores.title')}
        open={storeSelectionVisible}
        onCancel={() => setStoreSelectionVisible(false)}
        width={1000}
        footer={[
          <Button key="cancel" onClick={() => setStoreSelectionVisible(false)}>
            {t('planTemplate.wizard.scopeConfiguration.modals.selectStores.cancel')}
          </Button>,
          <Button
            key="confirm"
            type="primary"
            onClick={() => {
              setStoreSelectionVisible(false);
              message.success(t('planTemplate.wizard.scopeConfiguration.modals.selectStores.successMessage', {
                count: selectedStores.length
              }));
            }}
          >
            {t('planTemplate.wizard.scopeConfiguration.modals.selectStores.confirm')}
          </Button>
        ]}
      >
        <div style={{ marginBottom: '16px' }}>
          <Input
            placeholder={t('planTemplate.wizard.scopeConfiguration.modals.selectStores.searchPlaceholder')}
            prefix={<SearchOutlined />}
            value={storeSearch}
            onChange={(e) => setStoreSearch(e.target.value)}
            style={{ width: '300px' }}
          />
        </div>

        <Table
          rowSelection={{
            type: 'checkbox',
            selectedRowKeys: selectedStores.map(s => s.code),
            onChange: (_selectedRowKeys, selectedRows) => {
              handleStoreSelection(selectedRows as Store[]);
            }
          }}
          columns={storeColumns}
          dataSource={filteredAvailableStores || []}
          rowKey="code"
          // pagination={{
          //   pageSize: 10,
          //   showSizeChanger: true,
          //   showTotal: (total, range) => 
          //     {t('common.paginationTotal', { total })}
          // }}
          size="small"
          scroll={{ y: 500 }}
        />
      </Modal>

      {/* Exclude Store Modal */}
      <Modal
        title={t('planTemplate.wizard.scopeConfiguration.modals.excludeStores.title')}
        open={excludeStoreVisible}
        onCancel={() => setExcludeStoreVisible(false)}
        width={1000}
        footer={[
          <Button key="cancel" onClick={() => setExcludeStoreVisible(false)}>
            {t('planTemplate.wizard.scopeConfiguration.modals.excludeStores.cancel')}
          </Button>,
          <Button
            key="confirm"
            type="primary"
            onClick={() => {
              setExcludeStoreVisible(false);
            }}
          >
            {t('planTemplate.wizard.scopeConfiguration.modals.excludeStores.confirm')}
          </Button>
        ]}
      >
        <div style={{ marginBottom: '16px' }}>
          <Input
            placeholder={t('planTemplate.wizard.scopeConfiguration.modals.excludeStores.searchPlaceholder')}
            prefix={<SearchOutlined />}
            value={excludeStoreSearch}
            onChange={(e) => setExcludeStoreSearch(e.target.value)}
            style={{ width: '300px' }}
          />
        </div>

        <Table
          rowSelection={{
            type: 'checkbox',
            selectedRowKeys: [],
            onChange: (selectedRowKeys) => {
              handleExcludeStore(selectedRowKeys as string[]);
            }
          }}
          columns={storeColumns}
          dataSource={(filteredExcludeStores || []).filter(
            store => !scopeConfig.excludedStores.includes(store.code)
          )}
          rowKey="code"
          // pagination={{
          //   pageSize: 10,
          //   showSizeChanger: true,
          //   showTotal: (total, range) => t('common.paginationTotal', { start: range[0], end: range[1], total }),
          // }}
          size="small"
          scroll={{ y: 500 }}
        />
      </Modal>


    </div>
  );
};

export default ScopeConfiguration;